import { useTranslation } from 'react-i18next';
import { LuCircleAlert, LuCloudUpload } from 'react-icons/lu';

import { acceptedFormatLabels, maxFileSizeInMb } from './formatFileHint';
import { FilePicker } from '~/components/FilePicker';
import { cn } from '~/utils/cnTailwind';

type Props = {
    accept?: string[] | null;
    disabled?: boolean;
    errors?: string[];
    multiple?: boolean;
    onError: (message: string) => void;
    onSelect: (files: File[] | File | null) => void;
    size?: number;
};

export const Dropzone: React.FC<Props> = ({ accept, disabled, errors, multiple, onError, onSelect, size }) => {
    const { t } = useTranslation();
    const hint = t('FILE_UPLOAD.FORMAT_HINT', {
        formats: acceptedFormatLabels(accept),
        size: maxFileSizeInMb(size),
    });
    const hasErrors = Boolean(errors?.length);

    return (
        <div>
            <FilePicker
                accept={accept}
                disabled={disabled}
                multiple={multiple}
                onError={onError}
                onSelect={onSelect}
                size={size}
                className={cn(
                    'flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-6 text-center',
                    hasErrors
                        ? 'border-alert-error bg-alert-error-bg text-alert-error'
                        : 'border-grey-outline-light bg-surface-light text-midnight',
                    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                )}
            >
                <span className="flex flex-col items-center gap-1">
                    <LuCloudUpload size={20} className={hasErrors ? 'text-alert-error' : 'text-midnight'} />
                    <span className="text-sm font-medium">{t('FILE_UPLOAD.DROP_HINT')}</span>
                    <span className={cn('text-xs', hasErrors ? 'text-alert-error' : 'text-midnight-placeholder')}>
                        {hint}
                    </span>
                    <span className="mt-1 rounded border border-grey-outline-light bg-white px-3 py-1 text-xs text-midnight">
                        {t('FILE_MANAGER.FILE_CHOOSER_TITLE')}
                    </span>
                </span>
            </FilePicker>
            {errors?.map((message) => (
                <p key={message} className="mt-2 flex items-center gap-1.5 text-xs text-alert-error">
                    <LuCircleAlert size={14} />
                    {message}
                </p>
            ))}
        </div>
    );
};
